import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  IconLayoutDashboard,
  IconClipboardCheck,
  IconUsers,
  IconBooks,
  IconShield,
  IconLogout,
  IconMenu2,
  IconX,
} from '@tabler/icons-react';
import { useAuth } from '@/features/auth';
import { routes, navItems } from '@/config/routes';

type IconComponent = typeof IconLayoutDashboard;

const iconMap: Record<string, IconComponent> = {
  LayoutDashboard: IconLayoutDashboard,
  ClipboardCheck: IconClipboardCheck,
  Users: IconUsers,
  Library: IconBooks,
  Shield: IconShield,
};

export function AdminLayout() {
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate(routes.login);
  };

  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[var(--background-secondary)] border-b border-[var(--border)] z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-[var(--background-tertiary)] rounded-lg transition-colors"
          >
            <IconMenu2 size={24} className="text-[var(--text)]" />
          </button>
          <span className="font-bold text-[var(--text)]">Rackd Admin</span>
        </div>
        <span className="admin-badge px-2 py-1 text-xs font-medium rounded-md">Admin</span>
      </header>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full w-64 bg-[var(--background-secondary)] border-r border-[var(--border)]
          transform transition-transform duration-200 ease-out z-50
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Sidebar header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-[var(--text)]">Rackd</span>
            <span className="admin-badge px-2 py-1 text-xs font-medium rounded-md">Admin</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 hover:bg-[var(--background-tertiary)] rounded-lg transition-colors"
          >
            <IconX size={20} className="text-[var(--text-tertiary)]" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.map((item) => {
            const Icon: IconComponent = item.icon ? iconMap[item.icon] ?? IconLayoutDashboard : IconLayoutDashboard;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[var(--accent-primary)] text-white'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--background-tertiary)] hover:text-[var(--text)]'
                  }`
                }
              >
                <Icon size={20} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[var(--border)]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[var(--admin-purple)]/20 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-[var(--admin-purple)]">
                {profile?.displayName?.[0] || profile?.email?.[0] || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--text)] truncate">
                {profile?.displayName || 'Admin'}
              </p>
              <p className="text-xs text-[var(--text-tertiary)] truncate">
                {profile?.email}
              </p>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-[var(--text-tertiary)] hover:text-[var(--text)] hover:bg-[var(--background-tertiary)] rounded-lg transition-colors"
          >
            <IconLogout size={18} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:pl-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
